import { useState, useEffect } from 'react'
import {
  HiOutlineLocationMarker,
  HiOutlineCheck,
  HiOutlineX,
} from 'react-icons/hi'
import clsx from 'clsx'

/**
 * MobileLocationConfirm - Bottom sheet xác nhận vị trí khi thêm marker
 *
 * Flow:
 * 1. User tap "Thêm marker" từ FAB
 * 2. Component này hiển thị với pin cố định giữa màn hình
 * 3. User kéo map để chọn vị trí (pin không di chuyển, map di chuyển)
 * 4. User tap "Xác nhận" → trigger onConfirm với tọa độ
 */
function MobileLocationConfirm({
  isActive = false,
  onConfirm,
  onCancel,
  currentAddress = '',
  isLoadingAddress = false,
}) {
  const [isPulsing, setIsPulsing] = useState(true)

  // Stop pulsing after a few seconds to reduce distraction
  useEffect(() => {
    if (isActive) {
      setIsPulsing(true)
      const timer = setTimeout(() => setIsPulsing(false), 3000)
      return () => clearTimeout(timer)
    }
  }, [isActive])

  if (!isActive) return null

  return (
    <>
      {/* Fixed center pin */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-full z-30 pointer-events-none md:hidden">
        <div className="relative">
          {/* Pin shadow */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-2 bg-black/20 rounded-full blur-sm" />

          {/* Pin icon */}
          <div
            className={clsx(
              'relative transition-transform',
              isPulsing && 'animate-bounce'
            )}
          >
            <HiOutlineLocationMarker
              className="w-10 h-10 text-primary-600 drop-shadow-lg"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
            />
            {/* Pulse ring */}
            {isPulsing && (
              <div className="absolute inset-0 animate-ping">
                <HiOutlineLocationMarker className="w-10 h-10 text-primary-400 opacity-50" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Instruction overlay at top */}
      <div className="fixed top-16 left-0 right-0 z-30 flex justify-center pointer-events-none md:hidden">
        <div className="bg-gray-900/90 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
          Kéo bản đồ để chọn vị trí
        </div>
      </div>

      {/* Bottom confirmation sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-30 md:hidden">
        <div className="bg-white rounded-t-2xl shadow-2xl p-4 safe-bottom animate-slide-up">
          {/* Address preview */}
          <div className="mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                <HiOutlineLocationMarker className="w-5 h-5 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-0.5">Vị trí đã chọn</p>
                {isLoadingAddress ? (
                  <div className="h-5 w-3/4 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <p className="text-sm text-gray-900 line-clamp-2">
                    {currentAddress || 'Đang xác định địa chỉ...'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 py-3.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
            >
              <HiOutlineX className="w-5 h-5" />
              Hủy
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 py-3.5 rounded-xl font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
            >
              <HiOutlineCheck className="w-5 h-5" />
              Xác nhận
            </button>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out forwards;
        }
      `}</style>
    </>
  )
}

export default MobileLocationConfirm
